import { useEffect, useRef, useState } from 'react'
import { Button, Form, FormLabel } from 'react-bootstrap'
import ReCAPTCHA from 'react-google-recaptcha'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { Link, useNavigate } from 'react-router-dom'
import { getLocale, t } from '../locale'
import v from '../validators'

const HUBSPOT_SCRIPT_ID = 'hs-forms-embed-script'
const HUBSPOT_REGION = 'eu1'
const HUBSPOT_TARGET_ID = 'hubspot-signup-form'

// DS|KYC runs a separate HubSpot signup form per market: the .ch domain gets the
// Swiss (DS|KYC CH) form, every other domain the German (DS|KYC DE) one.
const HUBSPOT_FORMS = {
  ch: { portalId: '144531572', formId: '8ac06432-32c6-4f08-bc9e-e7a2c1a4b217' },
  de: { portalId: '144531459', formId: '54db101f-bbe2-452d-a73b-f721a4cd82fb' },
} as const

const hubspotForm = window.location.hostname.endsWith('.ch') ? HUBSPOT_FORMS.ch : HUBSPOT_FORMS.de

function Create() {
  const navigate = useNavigate()
  // Which signup form to show, controlled by the super admin "Signup Form" setting
  // (defaults to the standard form until /global-settings resolves).
  const [signupFormType, setSignupFormType] = useState<'standard' | 'hubspot'>('standard')
  const hubspotContainerRef = useRef<HTMLDivElement>(null)

  const [name, setCompanyName] = useState(process.env.REACT_APP_NEW_COMPANY_NAME ?? ('' as string))

  const [gender, setGender] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)

  const [isCompanyNameValid, setIsCompanyNameValid] = useState(true)
  const [isGenderValid, setIsGenderValid] = useState(true)
  const [isFirstNameValid, setIsFirstNameValid] = useState(true)
  const [isLastNameValid, setIsLastNameValid] = useState(true)
  const [isEmailValid, setIsEmailValid] = useState(true)
  const [isPhoneValid, setIsPhoneValid] = useState(true)
  const [isRecaptchaValid, setIsRecaptchaValid] = useState(true)

  const [isTermsValid, setIsTermsValid] = useState(true)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [recaptchaEnabled, setRecaptchaEnabled] = useState(false)

  useEffect(() => {
    localStorage.removeItem('companyData')
    if (process.env.REACT_APP_RECAPTCHA_ENABLED === '1') setRecaptchaEnabled(true)
  }, [])

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/global-settings`)
      .then(r => r.json())
      .then(body => {
        if (body?.signupFormType === 'hubspot') setSignupFormType('hubspot')
      })
      .catch(() => {
        // Stay on the standard form if the setting can't be loaded.
      })
  }, [])

  // Loads the HubSpot embed script (once, shared across mounts) and creates the
  // form into our own container once both the script and the container are ready.
  useEffect(() => {
    if (signupFormType !== 'hubspot' || !hubspotContainerRef.current) return

    let cancelled = false

    const createForm = () => {
      if (cancelled || !window.hbspt) return
      window.hbspt.forms.create({
        portalId: hubspotForm.portalId,
        formId: hubspotForm.formId,
        region: HUBSPOT_REGION,
        target: `#${HUBSPOT_TARGET_ID}`,
      })
    }

    if (window.hbspt) {
      createForm()
      return () => {
        cancelled = true
      }
    }

    const existingScript = document.getElementById(HUBSPOT_SCRIPT_ID) as HTMLScriptElement | null
    const script = existingScript ?? document.createElement('script')

    if (!existingScript) {
      script.id = HUBSPOT_SCRIPT_ID
      script.src = '//js-eu1.hsforms.net/forms/embed/v2.js'
      script.async = true
      document.body.appendChild(script)
    }

    script.addEventListener('load', createForm)

    return () => {
      cancelled = true
      script.removeEventListener('load', createForm)
    }
  }, [signupFormType])

  function handleCompanyName(e: any) {
    setCompanyName(e.target.value.trimStart())
    setIsCompanyNameValid(v.companyName(name))
  }
  function handleGender(e: any) {
    setGender(e.target.value)
    setIsGenderValid(v.gender(e.target.value))
  }
  function handleFirstName(e: any) {
    setFirstName(e.target.value.trimStart())
    setIsFirstNameValid(v.name(firstName))
  }
  function handleLastName(e: any) {
    setLastName(e.target.value.trimStart())
    setIsLastNameValid(v.name(lastName))
  }
  function handleEmail(e: any) {
    setEmail(e.target.value.trim())
    setIsEmailValid(v.email(email))
  }
  function handleTermsChange(e: any) {
    setTermsAccepted(e.target.checked)
  }
  function handleRecaptchaChange(token: string | null) {
    setRecaptchaToken(token)
    setIsRecaptchaValid(!!token)
  }
  async function handleSubmit(e: any) {
    e.preventDefault()

    setIsCompanyNameValid(v.companyName(name))
    setIsGenderValid(v.gender(gender))
    setIsFirstNameValid(v.name(firstName))
    setIsLastNameValid(v.name(lastName))
    setIsEmailValid(v.email(email))
    setIsPhoneValid(v.phone(phone))
    setIsTermsValid(termsAccepted)

    if (
      v.companyName(name) &&
      v.gender(gender) &&
      v.name(firstName) &&
      v.name(lastName) &&
      v.email(email) &&
      v.phone(phone) &&
      termsAccepted
    ) {
      if (recaptchaEnabled && !recaptchaToken) {
        setIsRecaptchaValid(false)
        return
      }
      const companyData = {
        name: name.trim(),
      }
      const userData: any = {
        gender: gender,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email,
        phone: phone.trim(),
        lang: getLocale(),
      }
      create(companyData, userData)
    }
  }

  function create(companyData: any, userData: any) {
    fetch(`${process.env.REACT_APP_API_URL}/company`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        company: companyData,
        user: userData,
        recaptchaToken: recaptchaToken,
      }),
    })
      .then(r => r.json())
      .then(r => {
        if (r.ok) {
          navigate('/')
        }
      })
  }

  if (signupFormType === 'hubspot') {
    return (
      <div className='container d-flex flex-col justify-content-center align-items-center'>
        <div className='col-14 col-md-5 col-lg-4 mt-4 bg-white text-center mb-4'>
          <div className='d-flex justify-content-center align-items-center position-relative'>
            <Link to='/'>
              <img
                className='mb-3'
                src='/KYC_logo_black.png'
                style={{ maxHeight: '45px' }}
                alt='DS|Kyc'
              ></img>
            </Link>
          </div>

          <div id={HUBSPOT_TARGET_ID} ref={hubspotContainerRef} />
        </div>
      </div>
    )
  }

  return (
    <>
      <div className='container d-flex flex-col justify-content-center align-items-center'>
        <div className='col-14 col-md-5 col-lg-4 mt-4 bg-white text-center mb-4'>
          <div className='d-flex justify-content-center align-items-center position-relative'>
            <Link to='/' className='d-inline-block p-4'>
              <img src='/KYC_logo_black.png' style={{ maxHeight: '45px' }} alt='DS|Kyc'></img>
            </Link>
          </div>

          <main className='form-signin'>
            <div className='mt-3 text-start'>
              <label htmlFor='company' className='fw-medium smaller text-primary required'>
                {t('company')}
              </label>
            </div>
            <Form.Control
              className='border-primary bg-white rounded-md form-control w-100'
              id='name'
              type='text'
              value={name}
              isInvalid={!isCompanyNameValid}
              onChange={handleCompanyName}
              onKeyUp={handleCompanyName}
              required
            />

            <div className='mt-3 text-start'>
              <FormLabel htmlFor='salutation' className='fw-medium smaller text-primary required'>
                {t('gender')}
              </FormLabel>
              <Form.Select
                className='border-primary bg-white rounded-md w-100'
                id='gender'
                value={gender}
                isInvalid={!isGenderValid}
                onChange={handleGender}
              >
                <option selected>--</option>
                <option value='male'>{t('male')}</option>
                <option value='female'>{t('female')}</option>
                <option value='other'>{t('other')}</option>
              </Form.Select>
            </div>

            <div className='mt-3 text-start'>
              <FormLabel htmlFor='firstName' className='fw-medium smaller text-primary required'>
                {t('firstName')}
              </FormLabel>
              <Form.Control
                className='border-primary bg-white rounded-md w-100'
                id='firstName'
                type='text'
                value={firstName}
                isInvalid={!isFirstNameValid}
                onChange={handleFirstName}
                onKeyUp={handleFirstName}
                autoComplete='off'
                required
              />
            </div>

            <div className='mt-3 text-start'>
              <FormLabel htmlFor='lastName' className='fw-medium smaller text-primary required'>
                {t('lastName')}
              </FormLabel>
              <Form.Control
                className='border-primary bg-white rounded-md w-100'
                id='lastName'
                type='text'
                value={lastName}
                isInvalid={!isLastNameValid}
                onChange={handleLastName}
                onKeyUp={handleLastName}
                autoComplete='off'
                required
              />
            </div>

            <div className='mt-3 text-start'>
              <FormLabel htmlFor='email' className='fw-medium smaller text-primary required'>
                {t('email')}
              </FormLabel>
              <Form.Control
                className='border-primary bg-white rounded-md w-100'
                id='email'
                type='email'
                value={email}
                isInvalid={!isEmailValid}
                onChange={handleEmail}
                onKeyUp={handleEmail}
                autoComplete='on'
                required
              />
            </div>

            <div className='mt-3 text-start'>
              <FormLabel htmlFor='phone' className='fw-medium smaller text-primary required'>
                {t('phone')}
              </FormLabel>
              <PhoneInput
                inputClass={`border-primary bg-white rounded-md w-100${!isPhoneValid ? ' is-invalid' : ''}`}
                buttonClass='bg-white border-primary'
                country={window.location.hostname.endsWith('.ch') ? 'ch' : 'de'}
                value={phone}
                onChange={value => {
                  setPhone(value)
                  setIsPhoneValid(v.phone(value))
                }}
                inputStyle={{ width: '100%' }}
                enableSearch={true}
                preferredCountries={['de', 'ch', 'at']}
              />
              {!isPhoneValid && <div className='text-danger small mt-1'>{t('invalidPhone')}</div>}
            </div>

            <div className='form-check text-start mt-3'>
              <Form.Check.Input
                type='checkbox'
                required
                id='terms'
                checked={termsAccepted}
                onChange={handleTermsChange}
                isInvalid={!isTermsValid}
              />
              <Form.Control.Feedback type='invalid'>{t('mustAgree')}</Form.Control.Feedback>
              <Form.Check.Label className='fw-medium smaller text-primary text-dark'>
                {t('agree')}&nbsp;
                {window.location.hostname.endsWith('.ch') ? (
                  <Link to='https://data-security.ch/de/agb' className='text-primary'>
                    {t('termsOfService')}
                  </Link>
                ) : (
                  <Link to='https://data-security.one/shop/agb/' className='text-primary'>
                    {t('termsOfService')}
                  </Link>
                )}{' '}
                &nbsp;{t('and')}&nbsp;
                {window.location.hostname.endsWith('.ch') ? (
                  <Link
                    to='https://data-security.ch/de/datenschutzhinweise'
                    className='text-primary'
                  >
                    {t('privacyPolicy')}
                  </Link>
                ) : (
                  <Link
                    to='https://data-security.one/datenschutzhinweise/'
                    className='text-primary'
                  >
                    {t('privacyPolicy')}
                  </Link>
                )}{' '}
                &nbsp;{t('zu')}
              </Form.Check.Label>
            </div>

            {recaptchaEnabled && (
              <div className='form-floating mt-3'>
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}
                  onChange={handleRecaptchaChange}
                  onExpired={() => setRecaptchaToken(null)}
                />
              </div>
            )}

            {recaptchaEnabled && !isRecaptchaValid && (
              <div className='text-danger small mt-1'>
                {t('Please verify that you are not a robot')}
              </div>
            )}
            <div className='d-flex justify-content-end mt-4'>
              <Button
                className='btn btn-primary rounded-pill px-3 btn btn-primary btn-sm ml-auto px-3 rounded-pill no-capitalize'
                style={{ textTransform: 'none' }}
                type='submit'
                onClick={handleSubmit}
              >
                {t('register')} »
              </Button>
            </div>
          </main>
        </div>
      </div>
    </>
  )
}

export default Create
